'use client';

import { c as _c } from "react/compiler-runtime";
import { useCourseRetirementByIdQuery } from '@/generated/graphql';
import { useUserHasOrganization } from '@/lib/hooks/use-user-has-organization';
function toDateOnlyString(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/**
 * `organizationInfo.retiresOn` is the raw scheduled_removal_date from Subscription Management,
 * with none of the self-healing the monolith applies once a date is overdue (it just keeps
 * returning the same past date indefinitely if the content team is late to actually remove the
 * course). Treat an overdue date as "no retirement to show" rather than surfacing a stale one.
 */
function isOverdue(retiresOn: string): boolean {
  return retiresOn.slice(0, 10) < toDateOnlyString(new Date());
}

/**
 * Fetches the org-scoped course retirement date, client-side only.
 *
 * `organizationInfo.retiresOn` resolves from the viewer's JWT org context, so it must never be
 * fetched as part of the ISR'd page shell (it would bake one org's banner into the shared cache).
 */
export function useCourseRetirement(courseId) {
  const $ = _c(10);
  const hasOrganization = useUserHasOrganization();
  let t0;
  if ($[0] !== courseId) {
    t0 = {
      id: courseId
    };
    $[0] = courseId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== hasOrganization) {
    t1 = {
      enabled: hasOrganization
    };
    $[2] = hasOrganization;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const courseRetirement = useCourseRetirementByIdQuery(t0, t1);
  if (!hasOrganization || courseRetirement.isError) {
    let t2;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = {
        isLoading: false,
        retiresOn: undefined
      };
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  const rawRetiresOn = courseRetirement.data?.course?.organizationInfo?.retiresOn as string | undefined ?? undefined;
  let t2;
  if ($[5] !== rawRetiresOn) {
    t2 = rawRetiresOn && !isOverdue(rawRetiresOn) ? rawRetiresOn : undefined;
    $[5] = rawRetiresOn;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const retiresOn = t2;
  let t3;
  if ($[7] !== courseRetirement.isLoading || $[8] !== retiresOn) {
    t3 = {
      isLoading: courseRetirement.isLoading,
      retiresOn
    };
    $[7] = courseRetirement.isLoading;
    $[8] = retiresOn;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}